import {AllPolymerLayouts} from 'constants/MapsConstants';
import BaseReactWrapperView, {BaseReactWrapperViewOptions} from 'molbio/maps/linear/BaseReactWrapperView';

export interface ReactWrapperViewOptions<LayoutName extends keyof AllPolymerLayouts>
  extends BaseReactWrapperViewOptions<LayoutName> {
  renderReact: (
    layout: AllPolymerLayouts[LayoutName],
    redraw: (newBoxHeight?: number | ((layout: AllPolymerLayouts[LayoutName]) => number)) => void
  ) => JSX.Element;
  boxHeight: (layout: AllPolymerLayouts[LayoutName]) => number;
  _staticBoxHeight?: number;
}

/**
 * A component that helps render a single-layered React component as part of a SliceView.
 *
 * @param options.renderReact A function that takes in the polymerLayout subobject defined by
 *   polymerLayoutName, and renders a <g class='component-wrapper' /> with content.
 * @param options.boxHeight A function given the same polymerLayout subobject to compute th height of the
 *   component, assuming it is to be shown (even when it would be hidden).
 * @param options._staticBoxHeight When defined, overrides any other boxHeight logic with a constant value
 *   for the lifetime of the view. This is currently only used to avoid an annoying timing issue when
 *   rendering protein liability sites for alignment PNG exports.
 */
export default class ReactWrapperView<
  LayoutName extends keyof AllPolymerLayouts,
> extends BaseReactWrapperView<LayoutName> {
  renderReact!: (
    layout: AllPolymerLayouts[LayoutName],
    redraw: (newBoxHeight?: number | ((layout: AllPolymerLayouts[LayoutName]) => number)) => void
  ) => JSX.Element;
  _boxHeight!: (layout: AllPolymerLayouts[LayoutName]) => number;
  _staticBoxHeight?: number;

  constructor(options: ReactWrapperViewOptions<LayoutName>) {
    super(options);
  }

  override initialize(options: ReactWrapperViewOptions<LayoutName>): void {
    super.initialize(options);
    this.renderReact = options.renderReact;
    this._boxHeight = options.boxHeight;
    // When defined, _staticBoxHeight overrides any other boxHeight logic with a constant value for the
    // lifetime of the view. This is only used to avoid an annoying timing issue when rendering protein
    // liability sites for alignment PNG exports.
    this._staticBoxHeight = options._staticBoxHeight;
  }

  override boxHeight(): number {
    if (!this.shouldShow()) {
      return 0;
    } else if (this._staticBoxHeight) {
      return this._staticBoxHeight;
    } else {
      return this._boxHeight(this.getLayout());
    }
  }

  // Triggers a redraw of the slice
  // Optionally take a parameter newBoxHeight. If this is a function, it replaces the view's current boxHeight
  // function. If this is a number, the current boxHeight function is replaced with a function that simply
  // returns that number.
  redraw(newBoxHeight?: number | ((layout: AllPolymerLayouts[LayoutName]) => number)): void {
    if (typeof newBoxHeight === 'function') {
      this._boxHeight = newBoxHeight;
    } else if (typeof newBoxHeight === 'number') {
      this._boxHeight = () => newBoxHeight;
    }

    this.trigger('redraw');
  }

  _renderComponentWrapper(): JSX.Element {
    return this.renderReact(this.getLayout(), this.redraw.bind(this));
  }
}
